import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  useCreateFilamentSpool,
  useFilamentSpools,
  FILAMENT_MATERIALS,
} from '../../hooks/useFilamentSpools'
import { BackLink } from '../../components/BackLink'
import { useToast } from '../../components/ToastContext'

export default function FilamentForm() {
  const navigate = useNavigate()
  const createSpool = useCreateFilamentSpool()
  const { toast } = useToast()
  const [searchParams] = useSearchParams()
  const fromSpoolId = searchParams.get('fromSpoolId')
  const { data: spools } = useFilamentSpools()
  const source = fromSpoolId ? spools?.find((s) => s.id === fromSpoolId) : null

  const today = new Date().toISOString().slice(0, 10)
  // Pre-fill the identity fields from the source spool when "Buy more like this".
  // Leave price/date/current-weight blank so the user enters the new purchase.
  const [brand, setBrand] = useState(source?.brand ?? '')
  const [material, setMaterial] = useState<string>(source?.material ?? 'PLA')
  const [color, setColor] = useState(source?.color ?? '')
  const [colorHex, setColorHex] = useState(source?.color_hex ?? '#000000')
  const [purchasePrice, setPurchasePrice] = useState('')
  const [purchaseDate, setPurchaseDate] = useState(today)
  const [originalWeight, setOriginalWeight] = useState(
    source ? String(source.original_weight_grams) : '1000',
  )
  const [currentWeight, setCurrentWeight] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [errorMessage, setErrorMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')
    const current =
      currentWeight.trim() === '' ? Number(originalWeight) : Number(currentWeight)
    const qty = Math.max(1, Math.floor(Number(quantity) || 1))
    try {
      // Create N identical spools. Each is its own row so the printer can swap
      // between them without re-mapping (e.g., 4× 1kg black PLA = 4 spools).
      for (let i = 0; i < qty; i++) {
        await createSpool.mutateAsync({
          brand: brand || null,
          material,
          color,
          color_hex: colorHex || null,
          purchase_price: purchasePrice ? Number(purchasePrice) : null,
          purchase_date: purchaseDate || null,
          original_weight_grams: Number(originalWeight),
          current_weight_grams: current,
        })
      }
      toast({ tone: 'success', text: qty > 1 ? `Added ${qty} spools` : 'Spool added' })
      navigate('/prints/filament')
    } catch (err) {
      setErrorMessage((err as Error).message)
    }
  }

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 560 }}>
      <BackLink fallback="/prints/filament" />
      <h1>{source ? 'Buy more like this' : 'Add filament spool'}</h1>
      {source && (
        <p
          style={{
            background: 'var(--color-primary-soft)',
            color: 'var(--color-primary-text)',
            padding: 'var(--space-2) var(--space-3)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            marginTop: 'var(--space-3)',
          }}
        >
          Pre-filled from <strong>{source.brand ? source.brand + ' ' : ''}{source.color} {source.material}</strong>. Adjust the purchase price + quantity for this new order; each spool will get its own row in inventory and an expense entry.
        </p>
      )}
      <form onSubmit={handleSubmit} style={{ marginTop: 'var(--space-6)' }}>
        <Field label="Brand" value={brand} onChange={setBrand} hint="e.g., eSun, Polymaker, Bambu" />
        <Field
          label="Material"
          value={material}
          onChange={setMaterial}
          select={FILAMENT_MATERIALS as unknown as string[]}
        />
        <Field
          label="Color name"
          value={color}
          onChange={setColor}
          required
          hint="e.g., Black, Galaxy Blue"
        />
        <Field
          label="Color swatch"
          value={colorHex}
          onChange={setColorHex}
          type="color"
          hint="For the dot on the spool card"
        />
        <Field
          label="Purchase price ($)"
          value={purchasePrice}
          onChange={setPurchasePrice}
          type="decimal"
          hint="What you paid for this spool"
        />
        <Field
          label="Purchase date"
          value={purchaseDate}
          onChange={setPurchaseDate}
          type="date"
        />
        <Field
          label="Original weight (grams)"
          value={originalWeight}
          onChange={setOriginalWeight}
          type="decimal"
          hint="Usually 1000 for a 1kg spool"
        />
        <Field
          label="Current weight (grams)"
          value={currentWeight}
          onChange={setCurrentWeight}
          type="decimal"
          hint="Leave blank if new — defaults to original weight"
        />
        <Field
          label="How many spools? (quantity)"
          value={quantity}
          onChange={setQuantity}
          type="decimal"
          hint="Bought a 4-pack of identical 1kg black? Enter 4 — creates 4 spools with the same details. Purchase price is per spool."
        />

        <button
          type="submit"
          disabled={createSpool.isPending}
          style={{
            marginTop: 'var(--space-6)',
            padding: 'var(--space-3) var(--space-5)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
          }}
        >
          {createSpool.isPending
            ? 'Saving…'
            : (Number(quantity) || 1) > 1
              ? `Add ${Math.floor(Number(quantity))} spools`
              : 'Add spool'}
        </button>

        {errorMessage && (
          <p
            style={{
              marginTop: 'var(--space-3)',
              color: 'var(--color-danger)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {errorMessage}
          </p>
        )}
      </form>
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  hint,
  required,
  select,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: 'text' | 'date' | 'decimal' | 'color'
  hint?: string
  required?: boolean
  select?: string[]
}) {
  const commonStyle: React.CSSProperties = {
    width: '100%',
    padding: 'var(--space-3)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    background: 'var(--color-bg)',
    color: 'var(--color-text)',
  }
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <label
        style={{
          display: 'block',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          marginBottom: 'var(--space-1)',
        }}
      >
        {label} {required && <span style={{ color: 'var(--color-danger)' }}>*</span>}
      </label>
      {select ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={commonStyle}
        >
          {select.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type === 'date' ? 'date' : type === 'color' ? 'color' : 'text'}
          inputMode={type === 'decimal' ? 'decimal' : undefined}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={
            type === 'color'
              ? { ...commonStyle, padding: 'var(--space-1)', height: 44, cursor: 'pointer' }
              : commonStyle
          }
        />
      )}
      {hint && (
        <p
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-faint)',
            marginTop: 'var(--space-1)',
          }}
        >
          {hint}
        </p>
      )}
    </div>
  )
}
